import React, { useState, useEffect, useCallback } from 'react';
import { collabAPI } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

const COLLAB_TYPES = [
  { id: 'CROSS_PROMOTION', icon: '📣', label: 'Cross Promotion' },
  { id: 'JOINT_VIDEO',     icon: '🎬', label: 'Joint Video' },
  { id: 'GUEST_POST',      icon: '✍️', label: 'Guest Post / Blog' },
  { id: 'PODCAST_GUEST',   icon: '🎙️', label: 'Podcast Guest' },
  { id: 'SHOUTOUT',        icon: '📢', label: 'Shoutout' },
  { id: 'CO_CREATION',     icon: '🤝', label: 'Co-Creation' },
  { id: 'EVENT',           icon: '🎪', label: 'Live / Event' },
];

const STAGES = [
  { id: 'IDEA',         label: 'Idea',         icon: '💡', color: '#8b5cf6' },
  { id: 'REACHED_OUT',  label: 'Reached Out',  icon: '📩', color: '#3b82f6' },
  { id: 'NEGOTIATING',  label: 'Negotiating',  icon: '🤝', color: '#f59e0b' },
  { id: 'CONFIRMED',    label: 'Confirmed',    icon: '✅', color: '#10b981' },
  { id: 'IN_PROGRESS',  label: 'In Progress',  icon: '🎬', color: '#6366f1' },
  { id: 'COMPLETED',    label: 'Completed',    icon: '🏆', color: '#059669' },
  { id: 'CANCELLED',    label: 'Cancelled',    icon: '❌', color: '#94a3b8' },
];

const PLATFORMS = ['Instagram', 'YouTube', 'Twitter/X', 'LinkedIn', 'Facebook', 'TikTok', 'Podcast', 'Blog', 'Other'];
const COMPENSATIONS = ['FREE', 'PAID', 'BARTER'];

const empty = {
  collabType: 'CROSS_PROMOTION', status: 'IDEA',
  creatorName: '', creatorHandle: '', creatorPlatform: 'Instagram',
  creatorEmail: '', creatorFollowers: '', creatorNiche: '',
  projectTitle: '', description: '', myDeliverables: '', theirDeliverables: '',
  platform: 'Instagram', proposedDate: '', completedDate: '',
  compensation: 'FREE', amount: '', notes: '',
};

export default function CollabPage() {
  const isMobile = useIsMobile();
  const [collabs, setCollabs]     = useState([]);
  const [summary, setSummary]     = useState({});
  const [view, setView]           = useState('kanban'); // kanban | list
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(empty);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [filterStage, setFilterStage] = useState('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, sr] = await Promise.all([collabAPI.getAll(), collabAPI.getSummary()]);
      setCollabs(cr.data);
      setSummary(sr.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditId(null);
    setForm(empty);
    setError('');
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      collabType: c.collabType || 'CROSS_PROMOTION',
      status: c.status || 'IDEA',
      creatorName: c.creatorName || '',
      creatorHandle: c.creatorHandle || '',
      creatorPlatform: c.creatorPlatform || 'Instagram',
      creatorEmail: c.creatorEmail || '',
      creatorFollowers: c.creatorFollowers || '',
      creatorNiche: c.creatorNiche || '',
      projectTitle: c.projectTitle || '',
      description: c.description || '',
      myDeliverables: c.myDeliverables || '',
      theirDeliverables: c.theirDeliverables || '',
      platform: c.platform || 'Instagram',
      proposedDate: c.proposedDate || '',
      completedDate: c.completedDate || '',
      compensation: c.compensation || 'FREE',
      amount: c.amount || '',
      notes: c.notes || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.creatorName.trim()) return setError('Creator name is required.');
    setError('');
    setSaving(true);
    try {
      if (editId) await collabAPI.update(editId, form);
      else        await collabAPI.create(form);
      setShowForm(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save.');
    }
    setSaving(false);
  };

  const handleStatus = async (id, status) => {
    try {
      await collabAPI.updateStatus(id, { status });
      await load();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collab?')) return;
    try { await collabAPI.delete(id); await load(); } catch {}
  };

  const nextStatus = (current) => {
    const order = ['IDEA','REACHED_OUT','NEGOTIATING','CONFIRMED','IN_PROGRESS','COMPLETED'];
    const idx = order.indexOf(current);
    return idx < order.length - 1 ? order[idx + 1] : null;
  };

  const byStage = (stageId) => collabs.filter(c => c.status === stageId);
  const filtered = filterStage === 'ALL' ? collabs : collabs.filter(c => c.status === filterStage);

  const typeLabel = (id) => COLLAB_TYPES.find(t => t.id === id)?.label || id;
  const typeIcon  = (id) => COLLAB_TYPES.find(t => t.id === id)?.icon || '🤝';
  const stageInfo = (id) => STAGES.find(s => s.id === id) || STAGES[0];

  return (
    <div style={{ padding: '2rem', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.8rem', margin: 0, color: 'var(--ink)' }}>
            🤝 Collab Manager
          </h1>
          <p style={{ color: '#64748b', marginTop: '0.3rem', fontSize: '0.9rem' }}>
            Track creator-to-creator collabs from idea to published — cross-promotions, joint videos, podcasts & more
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {['kanban','list'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding: '0.5rem 0.9rem', borderRadius: 8, border: '1px solid #e2e8f0',
                       background: view === v ? 'var(--ink)' : 'white',
                       color: view === v ? 'white' : '#64748b',
                       fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', textTransform: 'capitalize' }}>
              {v === 'kanban' ? '🗂 Kanban' : '📋 List'}
            </button>
          ))}
          <button onClick={openNew}
            style={{ padding: '0.55rem 1.1rem', borderRadius: 9, border: 'none',
                     background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            + New Collab
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.75rem' }}>
        {[
          { label: 'Total Collabs',   val: summary.total     || 0, icon: '🤝', color: '#6366f1' },
          { label: 'Active',          val: summary.active    || 0, icon: '🎬', color: '#f59e0b' },
          { label: 'Completed',       val: summary.completed || 0, icon: '🏆', color: '#10b981' },
          { label: 'Ideas Pipeline',  val: summary.ideas     || 0, icon: '💡', color: '#8b5cf6' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '1.3rem', marginBottom: '0.3rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, marginTop: '0.1rem' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* KANBAN VIEW */}
      {view === 'kanban' && (
        <div style={{ overflowX: 'auto', paddingBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', minWidth: 'max-content' }}>
            {STAGES.filter(s => s.id !== 'CANCELLED').map(stage => {
              const cards = byStage(stage.id);
              return (
                <div key={stage.id} style={{ width: 260, flexShrink: 0 }}>
                  {/* Column Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem',
                                padding: '0.6rem 0.85rem', borderRadius: 10,
                                background: stage.color + '12', border: `1px solid ${stage.color}30` }}>
                    <span>{stage.icon}</span>
                    <span style={{ fontWeight: 700, fontSize: '0.82rem', color: stage.color }}>{stage.label}</span>
                    <span style={{ marginLeft: 'auto', background: stage.color + '25', color: stage.color,
                                   borderRadius: 20, padding: '0.1rem 0.5rem', fontSize: '0.72rem', fontWeight: 700 }}>
                      {cards.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    {cards.map(c => {
                      const next = nextStatus(c.status);
                      return (
                        <div key={c.id} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
                                                  padding: '0.9rem', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                          onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '1rem' }}>{typeIcon(c.collabType)}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.creatorName}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                                {c.creatorHandle || c.creatorPlatform}
                              </div>
                            </div>
                          </div>
                          {c.projectTitle && (
                            <div style={{ fontSize: '0.75rem', color: '#374151', marginBottom: '0.4rem',
                                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              📌 {c.projectTitle}
                            </div>
                          )}
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: expandedId === c.id ? '0.6rem' : 0 }}>
                            {typeLabel(c.collabType)} · {c.compensation}
                          </div>

                          {expandedId === c.id && (
                            <div onClick={e => e.stopPropagation()}>
                              {c.description && (
                                <div style={{ fontSize: '0.73rem', color: '#64748b', lineHeight: 1.5, marginBottom: '0.5rem',
                                              borderTop: '1px solid #f1f5f9', paddingTop: '0.5rem' }}>
                                  {c.description}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                {next && (
                                  <button onClick={() => handleStatus(c.id, next)}
                                    style={{ flex: 1, padding: '0.35rem 0.5rem', borderRadius: 7, border: 'none', cursor: 'pointer',
                                             background: stage.color, color: 'white', fontWeight: 600, fontSize: '0.7rem' }}>
                                    → {STAGES.find(s => s.id === next)?.label}
                                  </button>
                                )}
                                <button onClick={() => openEdit(c)}
                                  style={{ padding: '0.35rem 0.6rem', borderRadius: 7, border: '1px solid #e2e8f0',
                                           background: 'white', color: '#374151', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer' }}>
                                  ✏️
                                </button>
                                <button onClick={() => handleDelete(c.id)}
                                  style={{ padding: '0.35rem 0.6rem', borderRadius: 7, border: '1px solid #fee2e2',
                                           background: '#fff5f5', color: '#ef4444', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer' }}>
                                  🗑
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {cards.length === 0 && (
                      <div style={{ border: '2px dashed #e2e8f0', borderRadius: 10, padding: '1.5rem', textAlign: 'center',
                                    color: '#cbd5e1', fontSize: '0.75rem' }}>
                        Drop ideas here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <>
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {['ALL', ...STAGES.map(s => s.id)].map(s => {
              const info = s === 'ALL' ? { label: 'All', color: 'var(--ink)' } : stageInfo(s);
              const isActive = filterStage === s;
              return (
                <button key={s} onClick={() => setFilterStage(s)}
                  style={{ padding: '0.3rem 0.8rem', borderRadius: 20, border: '1px solid',
                           borderColor: isActive ? info.color : '#e2e8f0',
                           background: isActive ? info.color : 'white',
                           color: isActive ? 'white' : '#64748b',
                           fontWeight: 600, fontSize: '0.73rem', cursor: 'pointer' }}>
                  {s === 'ALL' ? 'All' : info.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 16, border: '2px dashed #e2e8f0',
                          padding: '4rem 2rem', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤝</div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#64748b' }}>No collabs yet</div>
              <div style={{ fontSize: '0.82rem', marginTop: '0.4rem' }}>Click "+ New Collab" to add your first creator collaboration</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.7rem' }}>
              {filtered.map(c => {
                const si = stageInfo(c.status);
                const next = nextStatus(c.status);
                return (
                  <div key={c.id} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
                                            padding: '1rem 1.25rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                                  background: si.color + '15',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                      {typeIcon(c.collabType)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>{c.creatorName}</span>
                        {c.creatorHandle && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{c.creatorHandle}</span>}
                        <span style={{ padding: '0.15rem 0.6rem', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
                                       background: si.color + '15', color: si.color }}>
                          {si.icon} {si.label}
                        </span>
                      </div>
                      {c.projectTitle && (
                        <div style={{ fontSize: '0.8rem', color: '#374151', marginBottom: '0.2rem' }}>📌 {c.projectTitle}</div>
                      )}
                      <div style={{ fontSize: '0.73rem', color: '#94a3b8' }}>
                        {typeLabel(c.collabType)} · {c.creatorPlatform} · {c.compensation}
                        {c.creatorFollowers && ` · ${c.creatorFollowers} followers`}
                        {c.proposedDate && ` · ${new Date(c.proposedDate).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, alignItems: 'center' }}>
                      {next && (
                        <button onClick={() => handleStatus(c.id, next)}
                          style={{ padding: '0.38rem 0.7rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                                   background: si.color + '18', color: si.color, fontWeight: 600, fontSize: '0.73rem' }}>
                          → {STAGES.find(s => s.id === next)?.label}
                        </button>
                      )}
                      <button onClick={() => openEdit(c)}
                        style={{ padding: '0.38rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0',
                                 background: 'white', color: '#374151', fontWeight: 600, fontSize: '0.73rem', cursor: 'pointer' }}>
                        ✏️ Edit
                      </button>
                      <button onClick={() => handleDelete(c.id)}
                        style={{ padding: '0.38rem 0.65rem', borderRadius: 8, border: '1px solid #fee2e2',
                                 background: '#fff5f5', color: '#ef4444', fontWeight: 600, fontSize: '0.73rem', cursor: 'pointer' }}>
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: 18, padding: '2rem', width: '100%',
                        maxWidth: 620, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', marginBottom: '1.5rem', color: 'var(--ink)' }}>
              {editId ? '✏️ Edit Collab' : '🤝 New Collab'}
            </div>

            {/* Collab Type */}
            <label style={lbl}>Collab Type</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
              {COLLAB_TYPES.map(t => (
                <button key={t.id} onClick={() => setForm(p => ({ ...p, collabType: t.id }))}
                  style={{ padding: '0.6rem 0.4rem', borderRadius: 9, border: `2px solid ${form.collabType === t.id ? '#1e293b' : '#e2e8f0'}`,
                           background: form.collabType === t.id ? '#1e293b' : 'white',
                           color: form.collabType === t.id ? 'white' : '#374151',
                           cursor: 'pointer', textAlign: 'center', fontSize: '0.72rem', fontWeight: 600 }}>
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.2rem' }}>{t.icon}</div>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Stage */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={lbl}>Stage</label>
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} style={sel}>
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
              </select>
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', margin: '1rem 0', paddingTop: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#6366f1', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Collab Partner
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <Field label="Creator Name *" value={form.creatorName} onChange={v => setForm(p => ({ ...p, creatorName: v }))} placeholder="e.g. Raj Sharma" />
                <Field label="Handle / Username" value={form.creatorHandle} onChange={v => setForm(p => ({ ...p, creatorHandle: v }))} placeholder="@techWithRaj" />
                <div>
                  <label style={lbl}>Their Platform</label>
                  <select value={form.creatorPlatform} onChange={e => setForm(p => ({ ...p, creatorPlatform: e.target.value }))} style={sel}>
                    {PLATFORMS.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                  </select>
                </div>
                <Field label="Followers" value={form.creatorFollowers} onChange={v => setForm(p => ({ ...p, creatorFollowers: v }))} placeholder="e.g. 250K" />
                <Field label="Their Niche" value={form.creatorNiche} onChange={v => setForm(p => ({ ...p, creatorNiche: v }))} placeholder="e.g. Tech, Finance" />
                <Field label="Email" value={form.creatorEmail} onChange={v => setForm(p => ({ ...p, creatorEmail: v }))} placeholder="creator@email.com" />
              </div>
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', margin: '1rem 0', paddingTop: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#10b981', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Project Details
              </div>
              <Field label="Project Title" value={form.projectTitle} onChange={v => setForm(p => ({ ...p, projectTitle: v }))} placeholder="e.g. Tech vs Finance — Collab Video" full />
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={lbl}>Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="What is this collab about?" rows={2}
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={lbl}>My Deliverables</label>
                  <textarea value={form.myDeliverables} onChange={e => setForm(p => ({ ...p, myDeliverables: e.target.value }))}
                    placeholder="1 Reel + 3 Story frames..." rows={2}
                    style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={lbl}>Their Deliverables</label>
                  <textarea value={form.theirDeliverables} onChange={e => setForm(p => ({ ...p, theirDeliverables: e.target.value }))}
                    placeholder="1 YouTube mention + 2 Stories..." rows={2}
                    style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={lbl}>Go-Live Platform</label>
                  <select value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))} style={sel}>
                    {PLATFORMS.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                  </select>
                </div>
                <Field label="Proposed Date" value={form.proposedDate} onChange={v => setForm(p => ({ ...p, proposedDate: v }))} type="date" />
                <div>
                  <label style={lbl}>Compensation</label>
                  <select value={form.compensation} onChange={e => setForm(p => ({ ...p, compensation: e.target.value }))} style={sel}>
                    {COMPENSATIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              {form.compensation === 'PAID' && (
                <Field label="Amount (₹)" value={form.amount} onChange={v => setForm(p => ({ ...p, amount: v }))} placeholder="e.g. 5000" type="number" full />
              )}
              <div>
                <label style={lbl}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Internal notes…" rows={2}
                  style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>

            {error && (
              <div style={{ background: '#fee2e2', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.82rem', color: '#991b1b', marginBottom: '0.75rem' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, padding: '0.75rem', borderRadius: 10, border: 'none',
                         background: saving ? '#94a3b8' : 'var(--ink)', color: 'white',
                         fontWeight: 700, fontSize: '0.88rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : editId ? 'Update Collab' : 'Save Collab'}
              </button>
              <button onClick={() => setShowForm(false)}
                style={{ padding: '0.75rem 1.1rem', borderRadius: 10, border: '1px solid #e2e8f0',
                         background: 'white', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────
const lbl = { fontSize: '0.73rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.03em' };
const sel = { width: '100%', padding: '0.52rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', background: 'white', boxSizing: 'border-box' };

function Field({ label, value, onChange, placeholder, type = 'text', full }) {
  const inp = { width: '100%', padding: '0.52rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', boxSizing: 'border-box' };
  return (
    <div style={{ gridColumn: full ? '1 / -1' : undefined, marginBottom: full ? '0.75rem' : 0 }}>
      <label style={lbl}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inp} />
    </div>
  );
}
